import React, { useState } from 'react';
import { Plus, Minus } from 'lucide-react';
import { PageHero, FadeIn, SectionLabel } from '../components/Shared';
import FloatingQuestionsBg from '../components/bg/FloatingQuestionsBg';

const FAQ_GROUPS = [
    {
        category: 'Web Development',
        items: [
            { q: 'How long does it take to build my website?', a: 'Our Starter package (5 pages) is delivered in 7 business days, Professional (8 pages) in 10 days, and Premium (8+ pages) in 14 days. Timelines start after we receive your content and approve the design.' },
            { q: 'Do I need to provide content?', a: 'We can work with your existing content or help you create new copy. Basic copywriting guidance is included in all packages. For full professional copywriting, ask us for a quote.' },
            { q: 'Will my website work on mobile?', a: 'Absolutely. Every site we build is mobile-first and responsive. We test on all major devices and browsers.' },
            { q: 'Do I own my website?', a: 'Yes, 100%. You own all code, content, and assets. We hand over everything on completion.' },
            { q: 'Can I update the website myself?', a: 'Yes. We build on user-friendly platforms with a CMS. We also provide a quick tutorial after launch.' },
            { q: 'What platform do you build on?', a: 'We work with modern frameworks and platforms including WordPress, Webflow, and custom HTML/CSS/JS — whichever best suits your project.' },
        ],
    },
    {
        category: 'Security',
        items: [
            { q: 'What does the monthly security service include?', a: 'Three core services: AI-powered code auditing for vulnerabilities, SSL and security headers verification, and a comprehensive monthly report with risk scoring and dependency updates.' },
            { q: 'Do I need the security plan?', a: "It's optional but highly recommended. 43% of cyber attacks target small businesses. Our plan costs less than a single hour of emergency security work." },
            { q: 'Can I cancel the security plan anytime?', a: "Yes. There's no contract or minimum term. Cancel anytime with 30 days notice." },
            { q: 'What happens if you find a critical vulnerability?', a: 'We fix it immediately and notify you. Critical issues are addressed within 24 hours, not left for the monthly report.' },
            { q: 'Is my data safe with you?', a: "We are fully GDPR compliant. We only access what's necessary to perform security checks, and all data is handled securely." },
        ],
    },
    {
        category: 'Pricing & Payment',
        items: [
            { q: 'Do you offer payment plans?', a: 'Yes. We offer a 50/50 split — 50% upfront to start, 50% on delivery and your approval.' },
            { q: 'Are there any hidden fees?', a: 'None. The price you see is the price you pay. Hosting and domain are separate (we can advise on providers).' },
            { q: 'Do you offer discounts for multiple sites?', a: 'Yes. Contact us for a custom quote if you need more than one website built.' },
        ],
    },
];

const AccordionItem = ({ item, isOpen, onClick, testId }) => (
    <div
        className={`bg-white border rounded-2xl transition-all duration-300 ${
            isOpen ? 'border-[#7B61FF]/40 shadow-soft' : 'border-[#E8E8EC]'
        }`}
        data-testid={testId}
    >
        <button
            type="button"
            onClick={onClick}
            className="w-full flex items-center justify-between gap-6 px-6 py-5 text-left"
            data-testid={`${testId}-trigger`}
            aria-expanded={isOpen}
        >
            <span className="font-display text-[16px] sm:text-[17px] font-semibold tracking-tight text-[#1A1A2E]">
                {item.q}
            </span>
            <span
                className={`shrink-0 w-8 h-8 inline-flex items-center justify-center rounded-full transition-colors ${
                    isOpen ? 'bg-[#7B61FF] text-white' : 'bg-[#F7F7F8] text-[#4A4A5A]'
                }`}
            >
                {isOpen ? <Minus className="w-4 h-4" /> : <Plus className="w-4 h-4" />}
            </span>
        </button>
        <div
            className={`grid transition-all duration-500 ease-in-out ${
                isOpen ? 'grid-rows-[1fr] opacity-100' : 'grid-rows-[0fr] opacity-0'
            }`}
        >
            <div className="overflow-hidden">
                <p className="px-6 pb-6 text-[#4A4A5A] leading-relaxed text-[15px]">{item.a}</p>
            </div>
        </div>
    </div>
);

const FAQ = () => {
    const [open, setOpen] = useState({ '0-0': true });
    const toggle = (key) => setOpen((p) => ({ ...p, [key]: !p[key] }));

    return (
        <main className="bg-white text-[#1A1A2E] pb-24" data-testid="faq-page">
            <PageHero
                label="FAQ"
                title="Frequently asked questions."
                subtitle="Everything you need to know about working with us."
                testId="faq-hero"
                bg={<FloatingQuestionsBg />}
            />

            <section className="py-12 bg-white">
                <div className="max-w-4xl mx-auto px-6 lg:px-8 space-y-14">
                    {FAQ_GROUPS.map((group, gi) => (
                        <FadeIn key={gi} delay={gi * 0.05}>
                            <div>
                                <SectionLabel>{group.category}</SectionLabel>
                                <div className="mt-6 space-y-3">
                                    {group.items.map((it, ii) => {
                                        const key = `${gi}-${ii}`;
                                        return (
                                            <AccordionItem
                                                key={key}
                                                item={it}
                                                isOpen={!!open[key]}
                                                onClick={() => toggle(key)}
                                                testId={`faq-item-${gi}-${ii}`}
                                            />
                                        );
                                    })}
                                </div>
                            </div>
                        </FadeIn>
                    ))}
                </div>
            </section>
        </main>
    );
};

export default FAQ;
